/**
 * The pages: one per item kind, plus the home page.
 *
 * Each page reads one head item and, where the item has lists, its pages, and
 * renders what the item carries and nothing it does not: a page cannot show a
 * fact the Serve stage did not compute from the rows, which is how the site
 * stays a projection of the graph. The coalition page draws the computed
 * overlay with its recipe, version, parameters, window, and run in view
 * (invariant 14), and calls it what it is: shared funding, not a label.
 */
import { useState } from 'react';
import type { CoalitionMapItem } from '../../src/serve/items.js';
import { api } from './api.js';
import { Badge, Bar, Disclosure, Empty, GrantRow, Hint, Link, Loading, Pager, PartyLink, Problem, Section, Tabs } from './components.js';
import { buildDate, formatAmount, formatCount, formatEin, formatPeriod, returnUrl, sourceLabel } from './format.js';
import { COPY } from './copy.js';
import { describeMasterFile, describeReturnType, isPrivateFoundation } from './masterfile.js';
import { CO_FUNDING_DEFAULTS } from '../../src/recipes/defaults.js';
import { funderSummary, headlineSentence, organizationSummary } from './summary.js';
import { navigate } from './router.js';
import { useQuery } from './useQuery.js';
import { pageTitle, usePageTitle } from './title.js';
import { displayName } from './names.js';
import { SharedFunderMap } from './map/SharedFunderMap.js';
import { recipeParameters } from './map/layout.js';
import { clearTrail, pushCrumb, readTrail, truncateTrail } from './map/trail.js';

/** The level-one subjects, for a build item written before the counts existed. */
const TOP_SUBJECTS: readonly { readonly code: string; readonly label: string }[] = [
  { code: 'SA000000', label: 'Arts and culture' },
  { code: 'SB000000', label: 'Education' },
  { code: 'SC000000', label: 'Environment' },
  { code: 'SD000000', label: 'Philanthropy' },
  { code: 'SE000000', label: 'Health' },
  { code: 'SF000000', label: 'Science' },
  { code: 'SG000000', label: 'Social sciences' },
  { code: 'SH000000', label: 'Information and communications' },
  { code: 'SJ000000', label: 'Public safety and disaster management' },
  { code: 'SK000000', label: 'Public affairs' },
  { code: 'SM000000', label: 'Agriculture, fishing and forestry' },
  { code: 'SN000000', label: 'Community and economic development' },
  { code: 'SP000000', label: 'Religion' },
  { code: 'SQ000000', label: 'Sports and recreation' },
  { code: 'SR000000', label: 'International human rights' },
  { code: 'SS000000', label: 'Human services' },
  { code: 'ST000000', label: 'International relations' },
];

export const HomePage = () => {
  usePageTitle(pageTitle(COPY.home.headline));
  const build = useQuery('build', () => api.build());
  const headline = build.value?.headline ?? null;
  const subjects = build.value?.subjects ?? null;
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight">{COPY.home.headline}</h1>
      <p className="mt-2 max-w-2xl text-stone-700">{COPY.home.lead}</p>
      {headline !== null ? (
        <p className="mt-2 max-w-2xl text-stone-700" data-testid="headline">
          {headlineSentence(headline)}
        </p>
      ) : null}
      <Section title={COPY.home.examplesTitle}>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="examples">
          {COPY.home.examples.map((example) => (
            <li key={example.ein} className="rounded border border-stone-200 bg-white p-3">
              <Link to={`/org/${example.ein}`} className="font-medium text-teal-800 hover:underline">
                {example.name}
              </Link>
              <p className="mt-1 text-sm text-stone-600">{example.note}</p>
            </li>
          ))}
        </ul>
      </Section>
      <Section title="Subjects" aside={subjects === null ? undefined : 'Organizations classified here or beneath'}>
        <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3">
          {(subjects ?? TOP_SUBJECTS.map((subject) => ({ ...subject, organizations: null }))).map((subject) => (
            <li key={subject.code} className="flex items-baseline justify-between gap-2 pr-6">
              <Link quiet to={`/subject/${subject.code.toLowerCase()}`}>
                {subject.label}
              </Link>
              {subject.organizations === null ? null : <span className="text-xs tabular-nums text-stone-500">{formatCount(subject.organizations)}</span>}
            </li>
          ))}
        </ul>
      </Section>
      <Section title="This build">
        {build.loading ? <Loading what="the build" /> : null}
        {build.error !== null ? <Problem error={build.error} /> : null}
        {build.value !== null ? (
          <p className="text-sm text-stone-600" data-testid="build">
            Built {buildDate(build.value.goldRunId)} from the returns the IRS had released
            {headline?.latestPeriod ? `; the latest return is for ${formatPeriod(headline.latestPeriod)}` : ''}. {formatCount(build.value.counts['organization'] ?? 0)} organization pages,{' '}
            {formatCount(build.value.counts['subject'] ?? 0)} subjects, {formatCount(build.value.counts['coalition-map'] ?? 0)} shared-funder maps.{' '}
            <Link to="/about/reading#freshness">What that means</Link>.
          </p>
        ) : null}
      </Section>
    </>
  );
};

const GrantList = ({ ein, list, side, pages }: { ein: string; list: 'grantsReceived' | 'grantsGiven'; side: 'funder' | 'recipient'; pages: number }) => {
  const [page, setPage] = useState(1);
  const result = useQuery(`${list}:${ein}:${page}`, () => (list === 'grantsReceived' ? api.grantsReceived(ein, page) : api.grantsGiven(ein, page)));
  if (pages === 0) {
    return <Empty>None in the graph.</Empty>;
  }
  if (result.loading) {
    return <Loading what="grants" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  if (result.value === null) {
    return <Empty>This page is not in the graph.</Empty>;
  }
  return (
    <>
      <ul data-testid={list}>
        {result.value.edges.map((edge) => (
          <GrantRow key={edge.id} edge={edge} side={side} />
        ))}
      </ul>
      <Pager page={result.value.page} pages={result.value.pages} onPage={setPage} />
    </>
  );
};

/** The top of an organization's shared-funder map, on its page; the whole map is one click away. */
const MiniSharedFunderMap = ({ ein }: { ein: string }) => {
  const result = useQuery(`mini-map:${ein}`, () => api.coalitionMap(ein));
  if (result.loading) {
    return <Loading what="the shared funders" compact />;
  }
  if (result.error !== null || result.value === null) {
    return null;
  }
  return (
    <div data-testid="mini-map">
      <SharedFunderMap map={result.value} compact />
    </div>
  );
};

export const OrganizationPage = ({ ein }: { ein: string }) => {
  const result = useQuery(`organization:${ein}`, () => api.organization(ein));
  usePageTitle(pageTitle(displayName(result.value?.name ?? null) ?? (result.loading ? null : `No page for ${formatEin(ein)}`)));
  if (result.loading) {
    return <Loading what="the organization" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const item = result.value;
  if (item === null) {
    return (
      <>
        <h1 className="text-2xl font-semibold">No page for {formatEin(ein)}</h1>
        <p className="mt-2 text-stone-700">
          An organization has its own page when it files its own return. This number is not in the graph, or names a group that appears only inside another organization's filing.{' '}
          <Link to="/about/reading#listing">Why some organizations have no page</Link>.
        </p>
      </>
    );
  }
  const described = describeMasterFile(item.masterFile);
  // A private foundation, or anyone who gives more than it receives, is read as a funder first.
  const funderFirst = isPrivateFoundation(item.masterFile?.foundationCode ?? null) || item.grantsGiven.count > item.grantsReceived.count;
  const hasNinetyT = item.filings.some((filing) => filing.returnType === '990T');
  const received = (
    <Section key="received" title="Grants received" aside={`${formatCount(item.grantsReceived.count)} · ${formatAmount(item.grantsReceived.total)}`}>
      <Hint about="recipients">{COPY.organization.grantsReceived}</Hint>
      <GrantList ein={item.ein} list="grantsReceived" side="funder" pages={item.grantsReceived.pages} />
    </Section>
  );
  const given = (
    <Section key="given" title="Grants given" aside={`${formatCount(item.grantsGiven.count)} · ${formatAmount(item.grantsGiven.total)}`}>
      <Hint about="grants">{COPY.organization.grantsGiven}</Hint>
      <GrantList ein={item.ein} list="grantsGiven" side="recipient" pages={item.grantsGiven.pages} />
    </Section>
  );
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="organization-name" title={item.name ?? undefined}>
        {displayName(item.name) ?? formatEin(item.ein)}
      </h1>
      <p className="mt-1 text-stone-600">
        EIN {formatEin(item.ein)}
        {item.city !== null || item.state !== null ? ` · ${[displayName(item.city), item.state].filter((part) => part !== null).join(', ')}` : ''}
        {item.subject !== null ? (
          <>
            {' · '}
            <Link to={`/subject/${item.subject.code.toLowerCase()}`}>{item.subject.label}</Link>
            {item.nteeCode !== null ? <span className="text-xs"> (NTEE {item.nteeCode})</span> : null}
          </>
        ) : null}
      </p>
      <p className="mt-2 max-w-3xl text-stone-800" data-testid="organization-summary">
        {organizationSummary(item)}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {item.sources.map((source) => (
          <Badge key={source}>{sourceLabel(source)}</Badge>
        ))}
        {item.intermediary === true ? <Badge tone="amber">grant intermediary</Badge> : null}
      </div>
      {item.intermediary === true ? <Hint about="intermediaries">{COPY.organization.intermediary(CO_FUNDING_DEFAULTS.maximumFanOut)}</Hint> : null}
      <Tabs
        label="Views of this organization"
        items={[
          { label: 'Overview', to: `/org/${item.ein}`, current: true },
          ...(item.funder ? [{ label: 'As a funder', to: `/funder/${item.ein}`, current: false }] : []),
          ...(item.coalitionMap !== null ? [{ label: 'Shared-funder map', to: `/coalition/${item.ein}`, current: false }] : []),
        ]}
      />

      {item.mission !== null ? (
        <Section title="Mission" aside={`As filed, ${formatPeriod(item.mission.taxPeriod)}`}>
          <p className="text-stone-800">{item.mission.text}</p>
        </Section>
      ) : null}

      {item.programs.length > 0 ? (
        <Section title="Programs" aside={`As filed, ${formatPeriod(item.programs[0]?.taxPeriod ?? null)}`}>
          <ul className="list-disc space-y-2 pl-5 text-stone-800">
            {item.programs.map((program, index) => (
              <li key={index}>{program.text}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      {funderFirst ? [given, received] : [received, given]}
      {item.coalitionMap !== null ? (
        <Section title="Shared funders" aside={<Link to={`/coalition/${item.ein}`}>See the whole map</Link>}>
          <Hint about="map">{COPY.map.mini}</Hint>
          <MiniSharedFunderMap ein={item.ein} />
        </Section>
      ) : null}

      {item.sponsoredProjects.length > 0 ? (
        <Section title="Sponsored projects" aside="Named in this organization's own filings">
          <Hint about="listing">{COPY.organization.sponsoredProjects}</Hint>
          <ul className="space-y-1 text-sm" data-testid="projects">
            {item.sponsoredProjects.map((project) => (
              <li key={project.id}>
                {displayName(project.name)} <span className="text-stone-500">· {formatCount(project.grants)} grant rows</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title="Filings" aside={`${formatCount(item.filings.length)} in the graph`}>
        <Hint about="returns">{COPY.organization.filings}</Hint>
        {item.filings.length === 0 ? (
          <Empty>No e-filed return in the graph{item.masterFile?.latestTaxPeriod ? `; the master file records a return for ${formatPeriod(item.masterFile.latestTaxPeriod)}` : ''}.</Empty>
        ) : (
          <>
            <table className="w-full text-sm" data-testid="filings">
              <thead className="text-left text-stone-500">
                <tr>
                  <th className="py-1 font-normal">Return</th>
                  <th className="py-1 font-normal">Period</th>
                  <th className="py-1 font-normal">Copy</th>
                  <th className="py-1 font-normal">Source</th>
                </tr>
              </thead>
              <tbody>
                {item.filings.map((filing) => (
                  <tr key={filing.id} className={`border-t border-stone-100 ${filing.isCurrent ? '' : 'text-stone-500'}`}>
                    <td className="py-1">
                      {describeReturnType(filing.returnType) ?? '—'}
                      {filing.amended ? ' · amended' : ''}
                      {!filing.isCurrent ? ' · replaced by a later return for this period' : ''}
                      {filing.returnType === '990T' ? <span className="text-xs text-stone-500"> · no grants</span> : null}
                    </td>
                    <td className="py-1">{formatPeriod(filing.taxPeriod)}</td>
                    <td className="py-1 font-mono text-xs">
                      <a href={returnUrl(item.ein, filing.objectId)} className="underline decoration-stone-300 hover:decoration-stone-700" rel="noreferrer" title="This return on ProPublica Nonprofit Explorer">
                        {filing.objectId}
                      </a>
                    </td>
                    <td className="py-1 text-stone-600">{sourceLabel(filing.source)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasNinetyT ? <p className="mt-2 text-xs text-stone-500">{COPY.organization.ninetyT}</p> : null}
          </>
        )}
      </Section>

      {item.masterFile !== null ? (
        <Section title="Master file" aside={sourceLabel('irs-bmf')}>
          <Hint about="listing">{COPY.organization.masterFile}</Hint>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]" data-testid="master-file">
            <dt className="text-stone-500">Exemption</dt>
            <dd>{described.exemption ?? '—'}</dd>
            <dt className="text-stone-500">Foundation status</dt>
            <dd>{described.foundation ?? '—'}</dd>
            <dt className="text-stone-500">Status</dt>
            <dd>{described.status ?? '—'}</dd>
            <dt className="text-stone-500">Ruling</dt>
            <dd>{described.ruling ?? '—'}</dd>
            <dt className="text-stone-500">Latest return on record</dt>
            <dd>{formatPeriod(item.masterFile.latestTaxPeriod)}</dd>
            <dt className="text-stone-500">Assets</dt>
            <dd>{formatAmount(item.masterFile.assetAmount)}</dd>
            <dt className="text-stone-500">Income</dt>
            <dd>{formatAmount(item.masterFile.incomeAmount)}</dd>
            <dt className="text-stone-500">Revenue</dt>
            <dd>{formatAmount(item.masterFile.revenueAmount)}</dd>
          </dl>
        </Section>
      ) : null}
    </>
  );
};

export const FunderPage = ({ ein }: { ein: string }) => {
  const result = useQuery(`funder:${ein}`, () => api.funder(ein));
  usePageTitle(pageTitle(displayName(result.value?.name ?? null), 'Funder profile'));
  if (result.loading) {
    return <Loading what="the funder" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const item = result.value;
  if (item === null) {
    return (
      <>
        <h1 className="text-2xl font-semibold">No funder profile for {formatEin(ein)}</h1>
        <p className="mt-2 text-stone-700">
          A funder profile exists for a listed organization that reported at least one grant. <Link to={`/org/${ein}`}>Its organization page</Link> may still exist.
        </p>
      </>
    );
  }
  const total = Math.max(item.grantsGiven.count, 1);
  // Facets grouped by their top-level subject, so Christianity and Judaism sit under Religion rather than beside Education.
  const groups = new Map<string, { label: string; facets: typeof item.subjects }>();
  for (const facet of item.subjects) {
    const key = facet.broader?.code ?? facet.code;
    const label = facet.broader?.label ?? facet.label;
    const group = groups.get(key) ?? { label, facets: [] };
    groups.set(key, { label, facets: [...group.facets, facet] });
  }
  const peers = item.peers.filter((peer) => peer.intermediary !== true);
  const intermediaries = item.peers.filter((peer) => peer.intermediary === true);
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="funder-name" title={item.name ?? undefined}>
        {displayName(item.name) ?? formatEin(item.ein)}
      </h1>
      <p className="mt-2 max-w-3xl text-stone-800" data-testid="funder-summary">
        {funderSummary(item)}
      </p>
      {item.intermediary === true ? (
        <>
          <div className="mt-2">
            <Badge tone="amber">grant intermediary</Badge>
          </div>
          <Hint about="intermediaries">{COPY.organization.intermediary(CO_FUNDING_DEFAULTS.maximumFanOut)}</Hint>
        </>
      ) : null}
      <Tabs
        label="Views of this organization"
        items={[
          { label: 'Overview', to: `/org/${item.ein}`, current: false },
          { label: 'As a funder', to: `/funder/${item.ein}`, current: true },
          // A string on a current build, null on the wire from an older one, undefined from the offline server.
          ...(typeof item.coalitionMap === 'string' ? [{ label: 'Shared-funder map', to: `/coalition/${item.ein}`, current: false }] : []),
        ]}
      />
      <Section title="By period">
        <Hint about="returns">{COPY.funder.periods}</Hint>
        <table className="text-sm">
          <tbody>
            {item.periods.map((period) => (
              <tr key={period.period} className="border-t border-stone-100">
                <td className="py-1 pr-6">{formatPeriod(period.period)}</td>
                <td className="py-1 pr-6 tabular-nums">{formatCount(period.count)} grants</td>
                <td className="py-1 pr-6 tabular-nums">{formatAmount(period.total)}</td>
                <td className="py-1">
                  <Bar share={period.count / total} label={`${Math.round((100 * period.count) / total)}% of grants`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section title="Subjects funded" aside="Share of grants, by the recipients' classification">
        <Hint about="subjects">{COPY.funder.subjects}</Hint>
        {item.subjects.length === 0 ? (
          <Empty>No recipient with a classification in the graph.</Empty>
        ) : (
          <div className="space-y-3 text-sm" data-testid="subjects-funded">
            {[...groups.entries()].map(([code, group]) => (
              <div key={code}>
                <div className="font-medium text-stone-800">{group.label}</div>
                <ul className="mt-1 space-y-1">
                  {group.facets.map((facet) => (
                    <li key={facet.code} className="flex flex-wrap items-center gap-x-3">
                      <Bar share={facet.count / total} label={`${Math.round((100 * facet.count) / total)}% of grants`} />
                      <Link quiet to={`/subject/${facet.code.toLowerCase()}`}>
                        {facet.label}
                      </Link>
                      <span className="text-stone-600">
                        {formatCount(facet.count)} grants · {formatAmount(facet.total)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section title="States funded" aside="Share of grants">
        <Hint>{COPY.funder.states}</Hint>
        {item.states.length === 0 ? (
          <Empty>No recipient with a state in the graph.</Empty>
        ) : (
          <ul className="space-y-1 text-sm">
            {item.states.map((state) => (
              <li key={state.state} className="flex items-center gap-x-3">
                <Bar share={state.count / total} label={`${Math.round((100 * state.count) / total)}% of grants`} />
                <span className="w-8 font-medium">{state.state}</span>
                <span className="text-stone-600">
                  {formatCount(state.count)} grants · {formatAmount(state.total)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Recipients">
        <Hint about="listing">
          {formatCount(item.recipients.listed)} grants went to organizations with a page, {formatCount(item.recipients.unlisted)} to organizations with a number and no return on record,{' '}
          {formatCount(item.recipients.names)} to names the graph has not placed.
        </Hint>
      </Section>
      <Section title="Funders that share recipients" aside={`${formatCount(peers.length)} peers`}>
        <Hint about="map">{COPY.funder.peers}</Hint>
        {peers.length === 0 ? (
          <Empty>No other funder in the graph reached the same organizations.</Empty>
        ) : (
          <ul className="space-y-1 text-sm" data-testid="peers">
            {peers.map((peer) => (
              <li key={peer.ein}>
                <Link quiet to={`/funder/${peer.ein}`} title={peer.name ?? undefined}>
                  {displayName(peer.name) ?? formatEin(peer.ein)}
                </Link>{' '}
                · {formatCount(peer.sharedRecipients)} shared
              </li>
            ))}
          </ul>
        )}
        {intermediaries.length > 0 ? (
          <details className="mt-3 text-sm">
            <summary className="cursor-pointer text-stone-600">{formatCount(intermediaries.length)} intermediaries also share recipients</summary>
            <ul className="mt-2 space-y-1" data-testid="intermediary-peers">
              {intermediaries.map((peer) => (
                <li key={peer.ein}>
                  <Link quiet to={`/funder/${peer.ein}`} title={peer.name ?? undefined}>
                    {displayName(peer.name) ?? formatEin(peer.ein)}
                  </Link>{' '}
                  · {formatCount(peer.sharedRecipients)} shared <Badge tone="amber">intermediary</Badge>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </Section>
      <Section title="Grants given" aside={`${formatCount(item.grantsGiven.count)} · ${formatAmount(item.grantsGiven.total)}`}>
        <Hint about="grants">{COPY.funder.grantsGiven}</Hint>
        <GrantList ein={item.ein} list="grantsGiven" side="recipient" pages={item.grantsGiven.pages} />
      </Section>
    </>
  );
};

type FunderSort = 'grants' | 'total';

const SubjectFunders = ({ slug, states }: { slug: string; states: readonly { state: string; count: number; pages: number }[] }) => {
  // The state with the most funders is the one a reader most likely means; the selector is there for the rest.
  const largest = [...states].sort((a, b) => b.count - a.count || a.state.localeCompare(b.state))[0]?.state ?? '';
  const [state, setState] = useState(largest);
  const [page, setPage] = useState(1);
  const [year, setYear] = useState('');
  const [sort, setSort] = useState<FunderSort>('grants');
  const [hideIntermediaries, setHideIntermediaries] = useState(false);
  const result = useQuery(`subjectFunders:${slug}:${state}:${page}`, () => (state === '' ? Promise.resolve(null) : api.subjectFunders(slug, state, page)));
  if (states.length === 0) {
    return <Empty>No funder of work under this subject in the graph.</Empty>;
  }
  const loaded = result.value?.funders ?? [];
  const years = [...new Set(loaded.flatMap((funder) => funder.years))].sort();
  const shown = loaded
    .filter((funder) => year === '' || funder.years.includes(year))
    .filter((funder) => !hideIntermediaries || funder.intermediary !== true)
    .sort((a, b) => (sort === 'grants' ? b.grants - a.grants : b.total - a.total) || a.id.localeCompare(b.id));
  const hidden = loaded.length - loaded.filter((funder) => !hideIntermediaries || funder.intermediary !== true).length;
  const largestCount = Math.max(1, ...shown.map((funder) => (sort === 'grants' ? funder.grants : funder.total)));
  const scope = result.value === null ? null : COPY.subject.sortScope(sort, result.value.pages);
  return (
    <>
      <div className="flex flex-wrap items-end gap-4 text-sm">
        <label>
          <span className="block text-stone-600">State</span>
          <select
            value={state}
            onChange={(event) => {
              setState(event.target.value);
              setPage(1);
              setYear('');
            }}
            className="mt-1 rounded border border-stone-300 px-2 py-1"
            aria-label="State"
          >
            {states.map((entry) => (
              <option key={entry.state} value={entry.state}>
                {entry.state} · {formatCount(entry.count)} funders
              </option>
            ))}
          </select>
        </label>
        <fieldset className="flex flex-wrap items-center gap-1">
          <legend className="block text-stone-600">Tax year</legend>
          {['', ...years].map((option) => (
            <button
              key={option === '' ? 'any' : option}
              type="button"
              aria-pressed={year === option}
              onClick={() => setYear(option)}
              className={`rounded border px-2 py-0.5 ${year === option ? 'border-teal-700 bg-teal-50 text-teal-900' : 'border-stone-300 text-stone-700'}`}
            >
              {option === '' ? 'any' : option}
            </button>
          ))}
        </fieldset>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={hideIntermediaries} onChange={(event) => setHideIntermediaries(event.target.checked)} />
          <span>{COPY.subject.hideIntermediaries}</span>
          {hidden > 0 ? <span className="text-stone-500">({formatCount(hidden)} hidden)</span> : null}
        </label>
      </div>
      {result.loading ? <Loading what="funders" /> : null}
      {result.error !== null ? <Problem error={result.error} /> : null}
      {result.value !== null ? (
        <>
          {scope !== null ? (
            <p className="mt-2 text-xs text-stone-500" data-testid="sort-scope">
              {scope}
            </p>
          ) : null}
          <table className="mt-3 w-full text-sm" data-testid="subject-funders">
            <thead className="text-left text-stone-500">
              <tr>
                <th className="py-1 pr-2 font-normal">#</th>
                <th className="py-1 pr-2 font-normal">Funder</th>
                <th className="py-1 pr-2 font-normal">
                  <button type="button" onClick={() => setSort('grants')} aria-pressed={sort === 'grants'} className={sort === 'grants' ? 'font-medium text-stone-900' : ''}>
                    Grants
                  </button>
                </th>
                <th className="py-1 pr-2 font-normal">
                  <button type="button" onClick={() => setSort('total')} aria-pressed={sort === 'total'} className={sort === 'total' ? 'font-medium text-stone-900' : ''}>
                    Dollars
                  </button>
                </th>
                <th className="py-1 font-normal">Years</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((funder, index) => (
                <tr key={funder.id} className="border-t border-stone-100">
                  <td className="py-1 pr-2 tabular-nums text-stone-500">{typeof funder.rank === 'number' ? funder.rank : index + 1}</td>
                  <td className="py-1 pr-2">
                    {funder.ein === null ? (
                      <span title={funder.name ?? undefined}>{displayName(funder.name) ?? funder.id}</span>
                    ) : (
                      <Link quiet to={`/funder/${funder.ein}`} title={funder.name ?? undefined}>
                        {displayName(funder.name) ?? formatEin(funder.ein)}
                      </Link>
                    )}
                    {funder.intermediary === true ? (
                      <>
                        {' '}
                        <Badge tone="amber">intermediary</Badge>
                      </>
                    ) : null}
                  </td>
                  <td className="py-1 pr-2 tabular-nums">
                    {formatCount(funder.grants)}
                    {sort === 'grants' ? (
                      <>
                        {' '}
                        <Bar share={funder.grants / largestCount} label={`${formatCount(funder.grants)} grants`} />
                      </>
                    ) : null}
                  </td>
                  <td className="py-1 pr-2 tabular-nums">
                    {formatAmount(funder.total)}
                    {sort === 'total' ? (
                      <>
                        {' '}
                        <Bar share={funder.total / largestCount} label={formatAmount(funder.total)} />
                      </>
                    ) : null}
                  </td>
                  <td className="py-1 text-stone-600">{funder.years.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager
            page={result.value.page}
            pages={result.value.pages}
            onPage={(next) => {
              setPage(next);
              // The chips come from the loaded page; a year chosen here may not exist on the next page.
              setYear('');
            }}
          />
        </>
      ) : null}
    </>
  );
};

export const SubjectPage = ({ slug }: { slug: string }) => {
  const result = useQuery(`subject:${slug}`, () => api.subject(slug));
  usePageTitle(pageTitle(result.value?.label ?? null, 'Subject'));
  if (result.loading) {
    return <Loading what="the subject" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const item = result.value;
  if (item === null) {
    return <h1 className="text-2xl font-semibold">No subject {slug}</h1>;
  }
  const sentences = item.definition.split(/(?<=\.)\s+/);
  const lead = sentences.slice(0, 2).join(' ');
  const rest = sentences.slice(2).join(' ');
  return (
    <>
      <p className="text-sm text-stone-500">
        {item.broader !== null ? (
          <>
            <Link to={`/subject/${item.broader.code.toLowerCase()}`}>{item.broader.label}</Link> ›{' '}
          </>
        ) : (
          'Subject › '
        )}
        <span>{item.code}</span>
      </p>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="subject-label">
        {item.label}
      </h1>
      <p className="mt-2 max-w-3xl text-stone-700">{lead}</p>
      {rest.length > 0 ? (
        <div className="mt-1 max-w-3xl">
          <Disclosure summary="Read the full definition">
            <p className="text-stone-700">{rest}</p>
          </Disclosure>
        </div>
      ) : null}
      <p className="mt-2 text-sm text-stone-600">{formatCount(item.organizations)} organizations in the master file are classified here or beneath.</p>
      {item.narrower.length > 0 ? (
        <Section title="Narrower subjects">
          <ul className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2" data-testid="narrower">
            {item.narrower.map((child) => (
              <li key={child.code}>
                <Link quiet to={`/subject/${child.code.toLowerCase()}`}>
                  {child.label}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {item.nteeCodes.length > 0 ? (
        <Section title="NTEE codes mapped here">
          <Hint about="subjects">{COPY.subject.classification}</Hint>
          <ul className="flex flex-wrap gap-2 text-sm">
            {item.nteeCodes.map((code) => (
              <li key={code.code}>
                <Badge>
                  {code.code} {code.name}
                </Badge>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section title="Who funds work under this subject" aside="Including narrower subjects; by the recipient's state">
        <Hint about="intermediaries">{COPY.subject.funders}</Hint>
        <SubjectFunders slug={slug} states={item.funders} />
      </Section>
    </>
  );
};

export const CoalitionPage = ({ lookup, label }: { lookup: () => Promise<CoalitionMapItem | null>; label: string }) => {
  const result = useQuery(`coalition:${label}`, lookup);
  // Session storage holds the trail; clearing it changes nothing React watches, so a counter asks for the re-render.
  const [, setTrailEdits] = useState(0);
  usePageTitle(pageTitle(displayName(result.value?.anchor.name ?? null) ?? label, COPY.map.title));
  if (result.loading) {
    return <Loading what="the shared-funder map" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const map = result.value;
  if (map === null) {
    return (
      <>
        <h1 className="text-2xl font-semibold">No shared-funder map for {label}</h1>
        <p className="mt-2 text-stone-700">
          No organization shares two or more counted funders with this one in the window, or the anchor is not in the graph. <Link to="/about/reading#map">How the map is computed</Link>.
        </p>
      </>
    );
  }
  const parameters = recipeParameters(map.parameters);
  const anchorName = displayName(map.anchor.name) ?? formatEin(map.anchor.ein ?? '');
  const trail = readTrail().filter((crumb) => crumb.ein !== map.anchor.ein);
  const explore = (ein: string): void => {
    if (map.anchor.ein !== null) {
      pushCrumb({ ein: map.anchor.ein, name: map.anchor.name });
    }
    navigate(`/coalition/${ein}`);
  };
  return (
    <>
      {trail.length > 0 ? (
        <nav aria-label="Maps explored" className="mb-2 text-sm text-stone-600" data-testid="trail">
          {trail.map((crumb) => (
            <span key={crumb.ein}>
              <Link to={`/coalition/${crumb.ein}`} onNavigate={() => truncateTrail(crumb.ein)} className="text-teal-800 hover:underline">
                {displayName(crumb.name) ?? formatEin(crumb.ein)}
              </Link>
              {' › '}
            </span>
          ))}
          <span className="text-stone-900">{anchorName}</span>
          <button
            type="button"
            onClick={() => {
              clearTrail();
              setTrailEdits((edits) => edits + 1);
            }}
            className="ml-3 text-xs text-stone-500 hover:underline"
          >
            clear
          </button>
        </nav>
      ) : null}
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="coalition-anchor">
        {COPY.map.title} around <PartyLink party={map.anchor} />
      </h1>
      <p className="mt-2 max-w-3xl text-stone-800">{COPY.map.what(anchorName, map.window, parameters.minimumShared, parameters.ceiling)}</p>
      <div className="mt-2 max-w-3xl">
        <Disclosure summary="Method">
          <p className="text-stone-700">
            Recipe <code>{map.recipe}</code>, version {map.version}; parameters <code>{map.parameters}</code>; run <code>{map.run}</code>. A computed overlay with its provenance, never a label.{' '}
            <Link to="/about/reading#map">Read more</Link>.
          </p>
        </Disclosure>
      </div>
      {map.anchor.ein !== null ? (
        <Tabs
          label="Views of this organization"
          items={[
            { label: 'Overview', to: `/org/${map.anchor.ein}`, current: false },
            ...(map.anchorFunder === true ? [{ label: 'As a funder', to: `/funder/${map.anchor.ein}`, current: false }] : []),
            { label: 'Shared-funder map', to: `/coalition/${map.anchor.ein}`, current: true },
          ]}
        />
      ) : null}
      <div className="mt-4">
        <SharedFunderMap map={map} onExplore={explore} />
      </div>
      <Section title="Funders that reached the anchor" aside={`${formatCount(map.funders.length)} counted`}>
        <Hint about="map">{COPY.map.funders}</Hint>
        <ul className="space-y-1 text-sm">
          {map.funders.map((funder) => (
            <li key={funder.ein}>
              <PartyLink party={funder} />
            </li>
          ))}
        </ul>
      </Section>
      {map.excluded.length > 0 ? (
        <Section title="Reached the anchor, set aside" aside="Above the fan-out ceiling">
          <Hint about="intermediaries">{COPY.map.excluded}</Hint>
          <ul className="space-y-1 text-sm" data-testid="excluded">
            {map.excluded.map((funder) => (
              <li key={funder.ein}>
                <PartyLink party={funder} />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section title="Organizations that share funders" aside={`${formatCount(map.members.length)} shown`}>
        <Hint about="map">{COPY.map.members}</Hint>
        <ul className="space-y-2 text-sm" data-testid="members">
          {map.members.map((member) => (
            <li key={member.organization.ein}>
              <PartyLink party={member.organization} /> <Badge tone="teal">{formatCount(member.score)} shared</Badge>
              <div className="text-xs text-stone-500">
                Shared: {member.sharedFunders.map((ein) => displayName(map.funders.find((funder) => funder.ein === ein)?.name ?? null) ?? formatEin(ein)).join('; ')}
              </div>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
};

export const NotFoundPage = ({ path }: { path: string }) => {
  usePageTitle(pageTitle('Not found'));
  return (
    <>
      <h1 className="text-2xl font-semibold">Nothing at {path}</h1>
      <p className="mt-2 text-stone-700">
        Pages are <code>/org/&#123;ein&#125;</code>, <code>/funder/&#123;ein&#125;</code>, <code>/subject/&#123;slug&#125;</code>, and <code>/coalition/&#123;ein&#125;</code>. See{' '}
        <Link to="/about/reading">how FunderGraph reads a filing</Link>.
      </p>
    </>
  );
};
